import React, { useRef } from 'react';
import { UrgencyBar } from '../components/UrgencyBar';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
import { Check, ArrowRight, AlertTriangle, Sprout, BookOpen, ShieldCheck, ChefHat, Thermometer } from 'lucide-react';
export function LandingPage() {
  const purchaseSectionRef = useRef<HTMLDivElement>(null);
  const scrollToPurchase = () => {
    purchaseSectionRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const CHECKOUT_URL = 'https://checkout.rosetadigital.com/cart/45956509139097:1';
  const testimonials = [{
    name: 'Camila Rodríguez',
    location: 'Córdoba, Capital',
    text: 'Nunca había plantado ni un potus. La guía es súper clara, seguí los pasos y a las 3 semanas ya tenía mis primeras gírgolas. ¡Increíble verlas crecer!',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Roberto Méndez',
    location: 'CABA',
    text: 'Vivo en un monoambiente y pensaba que no tenía espacio. El bonus del mini invernadero me salvó. Estoy produciendo en un rincón de la cocina.',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Laura Fernández',
    location: 'Rosario',
    text: 'Había intentado con videos de YouTube y se me contaminó todo. Con esta guía entendí qué hacía mal. La sección de problemas y soluciones es oro puro.',
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Esteban Ruiz',
    location: 'Mendoza',
    text: 'Lo compré por curiosidad y ahora es mi hobby favorito. Las recetas del final son buenísimas, nunca había cocinado gírgolas así.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    name: 'Patricia Gómez',
    location: 'La Plata',
    text: 'Excelente inversión. Solo con lo que ahorré en no comprar kits caros ya se pagó sola la guía. Muy recomendada para empezar de cero.',
    image: 'https://images.unsplash.com/photo-1554151228-14d9def656ec?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }];
  return <div className="min-h-screen bg-[#fdfbf7] font-sans text-stone-800 selection:bg-[#4a7c2c] selection:text-white" data-id="element-31">
      <UrgencyBar data-id="element-32" />
      <PurchaseNotification data-id="element-33" />

      {/* Hero Section */}
      <header className="bg-[#2d5016] text-white pt-12 pb-20 px-4 relative overflow-hidden" data-id="element-34">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-35">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-36">
            <pattern id="pattern-circles" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-37">
              <circle cx="20" cy="20" r="2" fill="currentColor" data-id="element-38" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#pattern-circles)" data-id="element-39" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-40">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-8 tracking-tight" data-id="element-41">
            Cultivá gírgolas en casa, aunque nunca antes hayas cultivado nada
          </h1>

          {/* Product Image - moved between title and subtitle */}
          <div className="mb-8 flex justify-center" data-id="element-42">
            <img src="https://cdn.magicpatterns.com/uploads/rGh3gnqyp4p8ckmwVoh1zg/ChatGPT_Image_4_dic_2025,_20_37_00.png" alt="Guía Completa para Cultivar Gírgolas en Casa" className="w-full max-w-sm mx-auto drop-shadow-2xl transform hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={scrollToPurchase} data-id="element-43" />
          </div>

          <p className="text-xl md:text-2xl text-stone-200 mb-8 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-44">
            La guía definitiva para principiantes que quieren producir sus
            primeros hongos, sin errores y sin complicarse.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 max-w-2xl mx-auto mb-10" data-id="element-45">
            <p className="text-lg font-medium" data-id="element-46">
              Pensada para casas, departamentos y espacios chicos.{' '}
              <br className="hidden sm:block" data-id="element-47" />
              <span className="text-[#eab308]" data-id="element-48">
                Sin equipamiento raro. Sin fórmulas imposibles.
              </span>{' '}
              <br className="hidden sm:block" data-id="element-49" />
              Solo un método claro, realista y probado.
            </p>
          </div>

          <button onClick={scrollToPurchase} className="bg-[#eab308] text-[#2d5016] hover:bg-[#ca9a00] transition-colors font-bold text-lg md:text-xl py-4 px-8 rounded-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1 duration-200 flex items-center gap-2 mx-auto" data-id="element-50">
            Quiero empezar a cultivar hoy
            <ArrowRight className="w-6 h-6" data-id="element-51" />
          </button>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-16 px-4 bg-white" data-id="element-52">
        <div className="max-w-3xl mx-auto" data-id="element-53">
          <h2 className="text-3xl font-bold text-center text-[#2d5016] mb-12" data-id="element-54">
            ¿Te pasa esto?
          </h2>

          <div className="space-y-6" data-id="element-55">
            <div className="flex gap-4 items-start p-4 rounded-lg bg-red-50 border border-red-100" data-id="element-56">
              <div className="bg-red-100 p-2 rounded-full text-red-600 mt-1" data-id="element-57">
                <AlertTriangle className="w-5 h-5" data-id="element-58" />
              </div>
              <p className="text-lg text-stone-700" data-id="element-59">
                Buscás cómo cultivar gírgolas… pero cada video, blog o tutorial
                dice algo distinto.
              </p>
            </div>

            <div className="flex gap-4 items-start p-4 rounded-lg bg-red-50 border border-red-100" data-id="element-60">
              <div className="bg-red-100 p-2 rounded-full text-red-600 mt-1" data-id="element-61">
                <AlertTriangle className="w-5 h-5" data-id="element-62" />
              </div>
              <p className="text-lg text-stone-700" data-id="element-63">
                No sabés bien qué humedad usar, qué temperatura es la correcta o
                si la luz arruina todo.
              </p>
            </div>

            <div className="bg-stone-100 p-8 rounded-xl text-center my-8" data-id="element-64">
              <p className="font-bold text-stone-600 mb-4 uppercase tracking-wide text-sm" data-id="element-65">
                Te preguntás:
              </p>
              <p className="text-xl md:text-2xl font-serif italic text-stone-800" data-id="element-66">
                "¿Qué sustrato compro?" "¿Cuánto necesito?" <br data-id="element-67" />
                "¿Y si gasto plata y se contamina todo?"
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-68">
            <h3 className="text-2xl font-bold text-[#2d5016] mb-4" data-id="element-69">
              👉 Si te sentís así, no es falta de capacidad.
            </h3>
            <div className="inline-block bg-[#2d5016] text-white px-6 py-2 rounded-lg font-bold text-xl transform -rotate-2 shadow-lg" data-id="element-70">
              No es tu culpa
            </div>
            <p className="mt-6 text-lg text-stone-700" data-id="element-71">
              La mayoría de la información sobre cultivo de gírgolas está:
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-4 font-bold text-red-600" data-id="element-72">
              <span className="flex items-center gap-1" data-id="element-73">
                <XIcon data-id="element-74" /> Desordenada
              </span>
              <span className="flex items-center gap-1" data-id="element-75">
                <XIcon data-id="element-76" /> Incompleta
              </span>
              <span className="flex items-center gap-1" data-id="element-77">
                <XIcon data-id="element-78" /> Pensada para otros climas
              </span>
              <span className="flex items-center gap-1" data-id="element-79">
                <XIcon data-id="element-80" /> Difícil de aplicar
              </span>
            </div>
            <p className="mt-6 text-stone-600 italic" data-id="element-81">
              Por eso tanta gente abandona antes de cosechar.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 px-4 bg-[#f4f1ea] border-y border-[#e5e0d6]" data-id="element-82">
        <div className="max-w-5xl mx-auto" data-id="element-83">
          <div className="grid md:grid-cols-1 gap-12 items-center" data-id="element-84">
            <div data-id="element-85">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2d5016] mb-6 leading-tight" data-id="element-86">
                La solución: una guía clara, simple y adaptada a vos
              </h2>
              <p className="text-lg text-stone-700 mb-6 leading-relaxed" data-id="element-87">
                Creamos la{' '}
                <strong className="text-[#2d5016]" data-id="element-88">
                  GUÍA DEFINITIVA PARA CULTIVAR GÍRGOLAS EN CASA
                </strong>{' '}
                para que puedas hacerlo bien desde el primer intento.
              </p>
              <p className="text-lg text-stone-700 mb-8 leading-relaxed" data-id="element-89">
                Un PDF práctico que te lleva paso a paso por todo el proceso,
                con materiales reales, costos reales y condiciones posibles para
                cualquier hogar.
              </p>
              <div className="bg-white p-4 rounded-lg border-l-4 border-[#eab308] shadow-sm" data-id="element-90">
                <p className="font-bold text-stone-800" data-id="element-91">
                  Aunque nunca hayas cultivado hongos antes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 px-4 bg-white" data-id="element-92">
        <div className="max-w-4xl mx-auto" data-id="element-93">
          <h2 className="text-3xl font-bold text-center text-[#2d5016] mb-12 flex items-center justify-center gap-3" data-id="element-94">
            <BookOpen className="w-8 h-8" data-id="element-95" />
            ¿Qué incluye la guía?
          </h2>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-96">
            {['Todo el proceso explicado de forma simple: sustratos, inoculación, incubación, fructificación y cosecha.', 'Métodos de producción usando residuos del hogar.', 'Sistemas anti-contaminación fáciles de aplicar.', 'Tablas de rendimiento y mezclas recomendadas según clima, objetivo y tipo de sustrato.', 'Guía rápida de problemas y soluciones (para saber qué hacer si algo no sale como esperabas).', 'Sección gastronómica con recetas simples y valor nutricional de tu cosecha.'].map((item, idx) => <div key={idx} className="flex gap-3 items-start p-4 hover:bg-stone-50 rounded-lg transition-colors" data-id="element-97">
                <div className="bg-[#4a7c2c] p-1 rounded-full text-white mt-1 flex-shrink-0" data-id="element-98">
                  <Check className="w-4 h-4" data-id="element-99" />
                </div>
                <p className="text-stone-700" data-id="element-100">{item}</p>
              </div>)}
          </div>

          <div className="mt-10 text-center font-medium text-stone-500" data-id="element-101">
            Todo organizado para que no improvises y no pierdas tiempo.
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-[#2d5016] text-white" data-id="element-102">
        <div className="max-w-4xl mx-auto" data-id="element-103">
          <h2 className="text-3xl font-bold text-center mb-12 flex items-center justify-center gap-3" data-id="element-104">
            <Sprout className="w-8 h-8 text-[#eab308]" data-id="element-105" />
            ¿Qué vas a lograr con esta guía?
          </h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6" data-id="element-106">
            {[{
            icon: BookOpen,
            text: 'Entender todo el proceso sin confusión, desde el día uno.'
          }, {
            icon: ShieldCheck,
            text: 'Cultivar con seguridad y sin miedo a contaminar.'
          }, {
            icon: Check,
            text: 'Ahorrar tiempo, materiales y dinero evitando errores comunes.'
          }, {
            icon: Sprout,
            text: 'Obtener tu primera cosecha sin frustraciones.'
          }, {
            icon: Thermometer,
            text: 'Lograr floradas más grandes y más rendidoras.'
          }, {
            icon: ChefHat,
            text: 'Disfrutar el proceso de ver cómo tus residuos se transforman en alimento real.'
          }].map((benefit, idx) => <div key={idx} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/20 transition-colors" data-id="element-107">
                <benefit.icon className="w-8 h-8 text-[#eab308] mb-4" data-id="element-108" />
                <p className="font-medium leading-relaxed" data-id="element-109">{benefit.text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-16 px-4 bg-[#fdfbf7]" data-id="element-110">
        <div className="max-w-5xl mx-auto" data-id="element-111">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2d5016] mb-4" data-id="element-112">
            🎁 Y además, hoy accedés a estos BONUS GRATUITOS
          </h2>
          <p className="text-center text-stone-600 mb-12" data-id="element-113">
            Herramientas exclusivas para potenciar tus resultados
          </p>

          <div className="space-y-12" data-id="element-114">
            {/* Bonus 1 */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-stone-100 flex flex-col md:flex-row gap-8 items-center" data-id="element-115">
              <div className="w-full md:w-1/3" data-id="element-116">
                <img src="https://cdn.magicpatterns.com/uploads/a2MEVAW7eoLULxLsrcgFYy/5bfe0bae-59b3-437e-a7bb-a6ec7e3db0cf.png" alt="Bonus 1: Mini Invernadero Casero" className="w-full rounded-lg shadow-md transform hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={scrollToPurchase} data-id="element-117" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-118">
                <div className="inline-block bg-[#eab308] text-[#2d5016] text-xs font-bold px-2 py-1 rounded mb-2" data-id="element-119">
                  BONUS #1
                </div>
                <h3 className="text-2xl font-bold text-[#2d5016] mb-3" data-id="element-120">
                  Mini Invernadero Casero
                </h3>
                <p className="text-stone-700 mb-4" data-id="element-121">
                  Diseñado para crear el ambiente ideal de humedad y ventilación
                  en cualquier casa o departamento. Incluye planos, materiales y
                  medidas exactas. 3 versiones según el espacio que tengas.
                </p>
                <p className="text-[#4a7c2c] font-bold text-lg" data-id="element-122">
                  GRATIS con tu compra
                </p>
              </div>
            </div>

            {/* Bonus 2 */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-stone-100 flex flex-col md:flex-row gap-8 items-center" data-id="element-123">
              <div className="w-full md:w-1/3" data-id="element-124">
                <img src="https://cdn.magicpatterns.com/uploads/7KwNp35etgd7UCAVrdLcFx/9be59843-8b0b-4fef-b8ca-80c4ba6daf91.png" alt="Bonus 2: Conservar y deshidratar gírgolas" className="w-full rounded-lg shadow-md transform hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={scrollToPurchase} data-id="element-125" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-126">
                <div className="inline-block bg-[#eab308] text-[#2d5016] text-xs font-bold px-2 py-1 rounded mb-2" data-id="element-127">
                  BONUS #2
                </div>
                <h3 className="text-2xl font-bold text-[#2d5016] mb-3" data-id="element-128">
                  Conservar y deshidratar gírgolas
                </h3>
                <p className="text-stone-700 mb-4" data-id="element-129">
                  Aprendé a guardar tu cosecha por meses: deshidratado,
                  congelado, polvo de hongos y más.
                </p>
                <p className="text-[#4a7c2c] font-bold text-lg" data-id="element-130">
                  GRATIS con tu compra
                </p>
              </div>
            </div>

            {/* Bonus 3 */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-stone-100 flex flex-col md:flex-row gap-8 items-center" data-id="element-131">
              <div className="w-full md:w-1/3" data-id="element-132">
                <img src="https://cdn.magicpatterns.com/uploads/mJT7sS798W5QPLoxUngkWR/75216267-1114-4e3b-b24e-59fb6877ebbd.png" alt="Bonus 3: Economía del cultivo casero" className="w-full rounded-lg shadow-md transform hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={scrollToPurchase} data-id="element-133" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-134">
                <div className="inline-block bg-[#eab308] text-[#2d5016] text-xs font-bold px-2 py-1 rounded mb-2" data-id="element-135">
                  BONUS #3
                </div>
                <h3 className="text-2xl font-bold text-[#2d5016] mb-3" data-id="element-136">
                  Economía del cultivo casero
                </h3>
                <p className="text-stone-700 mb-4" data-id="element-137">
                  Cuánto produce una bolsa, cuánto gastás, cuánto ahorrás y cómo
                  escalar sin invertir de más.
                </p>
                <p className="text-[#4a7c2c] font-bold text-lg" data-id="element-138">
                  GRATIS con tu compra
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section ref={purchaseSectionRef} className="py-20 px-4 bg-[#2d5016] text-white relative overflow-hidden" data-id="element-139">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#eab308] rounded-full filter blur-[100px] opacity-20" data-id="element-140"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4a7c2c] rounded-full filter blur-[100px] opacity-20" data-id="element-141"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-142">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-143">
            🌱 Si querés empezar bien, sin perder tiempo ni dinero
          </h2>
          <p className="text-xl text-stone-200 mb-8" data-id="element-144">
            Esta guía es para vos. <br data-id="element-145" />
            Todo explicado de forma clara, práctica y pensada para la realidad
            de una casa común.
          </p>

          <div className="bg-white text-stone-900 rounded-2xl p-8 md:p-12 shadow-2xl max-w-2xl mx-auto transform hover:scale-[1.02] transition-transform duration-300" data-id="element-146">
            <p className="text-sm font-bold text-stone-500 uppercase tracking-wider mb-2" data-id="element-147">
              Oferta Especial Limitada
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-[#2d5016] mb-6" data-id="element-148">
              Guía Definitiva + 3 Bonos Exclusivos
            </h3>

            {/* What you get - enumerated list */}
            <div className="bg-stone-50 rounded-xl p-6 mb-6 text-left" data-id="element-149">
              <p className="font-bold text-stone-700 mb-4 text-center" data-id="element-150">
                Esto es lo que te llevás hoy:
              </p>
              <ul className="space-y-3" data-id="element-151">
                <li className="flex items-start gap-3" data-id="element-152">
                  <div className="bg-[#4a7c2c] p-1 rounded-full text-white mt-0.5 flex-shrink-0" data-id="element-153">
                    <Check className="w-4 h-4" data-id="element-154" />
                  </div>
                  <span className="text-stone-700 font-medium" data-id="element-155">
                    <strong className="text-[#2d5016]" data-id="element-156">
                      Guía Completa para Cultivar Gírgolas en Casa
                    </strong>{' '}
                    (PDF descargable)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-157">
                  <div className="bg-[#4a7c2c] p-1 rounded-full text-white mt-0.5 flex-shrink-0" data-id="element-158">
                    <Check className="w-4 h-4" data-id="element-159" />
                  </div>
                  <span className="text-stone-700" data-id="element-160">
                    <strong data-id="element-161">BONUS #1:</strong> Mini Invernadero Casero{' '}
                    <span className="text-[#4a7c2c] font-bold" data-id="element-162">(GRATIS)</span>
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-163">
                  <div className="bg-[#4a7c2c] p-1 rounded-full text-white mt-0.5 flex-shrink-0" data-id="element-164">
                    <Check className="w-4 h-4" data-id="element-165" />
                  </div>
                  <span className="text-stone-700" data-id="element-166">
                    <strong data-id="element-167">BONUS #2:</strong> Conservar y Deshidratar Gírgolas{' '}
                    <span className="text-[#4a7c2c] font-bold" data-id="element-168">(GRATIS)</span>
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-169">
                  <div className="bg-[#4a7c2c] p-1 rounded-full text-white mt-0.5 flex-shrink-0" data-id="element-170">
                    <Check className="w-4 h-4" data-id="element-171" />
                  </div>
                  <span className="text-stone-700" data-id="element-172">
                    <strong data-id="element-173">BONUS #3:</strong> Economía del Cultivo Casero{' '}
                    <span className="text-[#4a7c2c] font-bold" data-id="element-174">(GRATIS)</span>
                  </span>
                </li>
              </ul>

              <div className="mt-6 pt-6 border-t border-stone-200 space-y-2" data-id="element-175">
                <p className="flex items-center justify-center gap-2 text-stone-600 font-medium" data-id="element-176">
                  📩 Entrega inmediata en PDF
                </p>
                <p className="flex items-center justify-center gap-2 text-stone-600 font-medium" data-id="element-177">
                  📌 Descarga para siempre
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 mb-6" data-id="element-178">
              <span className="text-stone-400 line-through text-lg" data-id="element-179">
                $45.000
              </span>
              <div className="flex flex-col items-center" data-id="element-180">
                <span className="text-5xl font-extrabold text-[#2d5016]" data-id="element-181">
                  $19.990
                </span>
                <span className="text-xs font-bold text-[#eab308] bg-[#2d5016] px-2 py-0.5 rounded mt-1" data-id="element-182">
                  ARS
                </span>
              </div>
            </div>

            <a href={CHECKOUT_URL} className="block w-full bg-[#eab308] text-[#2d5016] hover:bg-[#ca9a00] transition-colors font-bold text-xl py-5 px-8 rounded-xl shadow-lg hover:shadow-xl mb-4 animate-pulse-slow" data-id="element-183">
              👉 DESCARGAR AHORA
            </a>

            <p className="text-sm text-stone-500 flex items-center justify-center gap-2" data-id="element-184">
              <ShieldCheck className="w-4 h-4" data-id="element-185" />
              Pago 100% seguro y acceso inmediato
            </p>
          </div>

          <p className="mt-8 text-stone-300 text-sm" data-id="element-186">
            Accedé ahora a la Guía Definitiva para Cultivar Gírgolas en Casa y
            empezá tu primera cosecha con seguridad.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-white" data-id="element-187">
        <div className="max-w-6xl mx-auto" data-id="element-188">
          <h2 className="text-3xl font-bold text-center text-[#2d5016] mb-12" data-id="element-189">
            Lo que dicen quienes ya están cultivando
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-190">
            {testimonials.slice(0, 3).map((t, i) => <TestimonialCard key={i} {...t} data-id="element-191" />)}
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-6 max-w-4xl mx-auto" data-id="element-192">
            {testimonials.slice(3, 5).map((t, i) => <TestimonialCard key={i} {...t} data-id="element-193" />)}
          </div>
        </div>
      </section>

      <footer className="bg-[#1a2f0d] text-stone-400 py-8 text-center text-sm" data-id="element-194">
        <div className="max-w-4xl mx-auto px-4" data-id="element-195">
          <p data-id="element-196">
            © {new Date().getFullYear()} Cultivo Casero. Todos los derechos
            reservados.
          </p>
          <p className="mt-2" data-id="element-197">
            Este producto no garantiza resultados económicos. Los resultados
            pueden variar.
          </p>
        </div>
      </footer>
    </div>;
}
function XIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-198">
      <path d="M18 6 6 18" data-id="element-199" />
      <path d="m6 6 12 12" data-id="element-200" />
    </svg>;
}